import { AuthRoutes, PrivateRoute } from '@codezo/auth';
import { AuthenticationUser, AuthRoleEnum } from '@codezo/graphql';
import { ThemeProvider } from '@codezo/theme';
import '@codezo/theme/src/global.css';
import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { ModalsProvider } from '@mantine/modals';
import { useCallback } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { NotFoundPage } from './components/NotFoundPage';
import { DashboardPage } from './features/dashboard';
import { NewsletterPage } from './features/newsletter/pages';
import { RegularUsersPage, UsersPage } from './features/users';
import { WorkoutsPage } from './features/workouts';
import { CategoriesPage } from './features/categories';
import { WellBeingPage } from './features/wellbeing';
import { TranslationsPage } from './features/translations';
import { MockupsPage } from './features/mockups';
import { AdminUserType, useAuth } from './hooks/useAuth';
import { PlatformLayout } from './layouts/PlatformLayout';
import { theme } from './theme.config';
import './styles.css';

export default function App() {
	const { isAuthenticated, user, setUser, setAccessToken, token } = useAuth();

	const formatLoginUser = useCallback((authUser: AuthenticationUser): AdminUserType => {
		return {
			id: authUser.id,
			email: authUser.email!,
			role: authUser.role,
			organizationId: authUser.organizationUser?.organizationId,
			organization: authUser.organizationUser?.organization
				? {
						id: authUser.organizationUser.organization.id,
						name: authUser.organizationUser.organization.name,
					}
				: undefined,
		};
	}, []);

	return (
		<MantineProvider theme={theme} defaultColorScheme="light" forceColorScheme="light">
			<ModalsProvider>
				<ThemeProvider>
					<Router>
					<AuthRoutes
						setUser={setUser}
						setAccessToken={setAccessToken}
						userFormatter={formatLoginUser}
						token={token}
						requiredRole={AuthRoleEnum.Admin}
					/>
					<Routes>
						{/* Protected routes with main layout */}
						<Route
							path="/"
							element={
								<PrivateRoute
									isAuthenticated={isAuthenticated}
									user={user}
									requiredRole={AuthRoleEnum.Admin}
								>
									<PlatformLayout />
								</PrivateRoute>
							}
						>
							<Route index element={<Navigate to="/dashboard" replace />} />
							<Route path="dashboard" element={<DashboardPage />} />
							<Route path="users" element={<UsersPage />} />
							<Route path="regular-users" element={<RegularUsersPage />} />
							<Route path="workouts" element={<WorkoutsPage />} />
							<Route path="categories" element={<CategoriesPage />} />
							<Route path="wellbeing" element={<WellBeingPage />} />
							<Route path="translations" element={<TranslationsPage />} />
							<Route path="newsletter" element={<NewsletterPage />} />
						</Route>

						{/* Standalone tool pages */}
						<Route path="mockups" element={<MockupsPage />} />

						{/* 404 - Catch all unmatched routes */}
						<Route path="*" element={<NotFoundPage />} />
					</Routes>
					</Router>
				</ThemeProvider>
			</ModalsProvider>
		</MantineProvider>
	);
}
